import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { useRISearch, risMessageFromStored, RISMode, RISMessage, DeepRISearchResponse, LiveResearchState, RISLinkMetadata, EnhancedSource, ResearchSource, StructuredCaseLawResponse } from '../contexts/RISearchContext';
import { useRISViewer } from '../contexts/RISViewerContext';
import { buildApiUrl, STAGING_LANE_B_ENABLED, STAGING_LANE_C_ENABLED } from '../config/api';
import { FileText, Calculator, FlaskConical, Paperclip, Send, Square, TrendingUp, Target } from 'lucide-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import webChatHistoryService from '../services/webChatHistoryService';
import { useAppLayout } from '../components/AppLayout';
import LegalMarkdown from '../components/LegalMarkdown';
import AnswerLayers from '../components/AnswerLayers';
import ChatAttachmentChips, { ChatAttachmentNames, ChatContextChips } from '../components/ChatAttachmentChips';
import SourcesFooter from '../components/SourcesFooter';
import { SourceItem } from '../components/ResearchComparison';
import AnswerRating from '../components/AnswerRating';
import ResearchRunStatus, { ResearchRestartNotice } from '../components/ResearchRunStatus';
import type { ResearchRunEnd } from '../services/researchRunStore';
import { ErrorHandler, istRechercheSperre } from '../utils/errorHandler';
import { formatMessageHtml, nachrichtHtml } from '../utils/antwortFormat';
import { getResearchComparisonCapabilities } from '../services/localResearchComparison';
import apiClient from '../services/apiClient';
import { alterChatModus, enterSendet, mitEnterSenden, PRAEFERENZEN_TEXTE } from './einstellungen/praeferenzen';
import { VorlagenKnopf } from './vorlagen/VorlagenKnopf';
import { ersterPlatzhalter } from './vorlagen/vorlagenTexte';
import { BereichLink } from './einstellungen/EinstLink';
import { hilfeBereichGebaut } from '../components/hilfe/hilfeLeiste';
import { meldeCode, problemMeldenAdresse } from '../components/hilfe/ticketLogik';
import { TICKET_TEXTE } from '../components/hilfe/ticketTexte';
import { useWebsucheAn, WebsucheHinweis } from './websucheHinweis';
import {
  CHAT_ATTACHMENT_ACCEPT,
  CHAT_ATTACHMENT_MAX_OPEN,
  CHAT_ATTACHMENT_TEXT_ACCEPT,
  CHAT_ATTACHMENT_TEXT_TYPES_LABEL,
  CHAT_ATTACHMENT_TYPES_LABEL,
  CHAT_CONTEXT_PRIVACY_NOTE,
  attachmentRequestError,
  attachmentSelectionError,
  deleteChatAttachment,
  splitOversizedAttachments,
  listChatAttachments,
  setChatAttachmentInContext,
  uploadChatAttachments,
  type ChatAttachmentSummary,
  type ChatContextDocument,
} from '../services/chatAttachments';
import { endedDetailRunIds, forgetEndedRun, LatestSelection, mergeSessionMessages, nextEndedRuns, runEndForQuestion } from '../services/researchSessionState';
import {
  DEFAULT_STAGING_LANE_SELECTION,
  objectionCountLabel,
  resolvedStagingLane,
  reviewObjectionLabel,
  toolNamesLabel,
  type ResearchMetadata,
  type ReviewObjection,
  type StagingLaneSelection,
  type StagingLaneVariant,
} from '../services/risResearchStream';
import {
  answerLinkLabels,
  externalLinkUrl,
  isOfficialRisUrl,
  linkRisReferencesMarkdown,
  openViewerSource,
  resolveViewerSourceTarget,
  risReferenceLabel,
  safeHttpUrl,
  sourceLabelKey,
  type RisTextReference,
  type ViewerSourceTarget,
} from '../services/risViewerLinks';
import {
  linkResolvedDocumentMarkdown,
  resolvableSourceUrl,
  resolvableSourceUrls,
  resolvableUrlsInText,
  resolveDocumentSources,
  type ResolvedDocumentMap,
} from '../services/documentSourceResolver';

// Die Darstellung einer Antwort liegt in utils/antwortFormat.ts; der Export bleibt hier für bestehende Aufrufer.
export { formatMessageHtml };

/** Die vom ToolUse-Stack gemeldeten Fundstellen als benennbare Verweise. */
const risTextReferences = (metadata?: RISLinkMetadata): RisTextReference[] => (metadata ? [
  ...metadata.geschaeftszahlen.map(item => ({ text: item.geschaeftszahl, url: item.risUrl })),
  ...metadata.paragraphen.map(item => ({ text: item.paragraph, url: item.risLink })),
] : []);

/**
 * Der Text, den der Antwortkörper tatsächlich darstellt: aufgelöste Adressen
 * als Dokumentmarken, gemeldete Fundstellen als benannte Verweise. Der
 * Quellenblock leitet seine Beschriftungen aus demselben Text ab. Aus dem
 * rohen Antworttext abgeleitet, nähme der Antworttext eine Entscheidung bei
 * ihrer Geschäftszahl auf und die Quellenliste daneben nur "RIS-Dokument".
 */
export function preparedAnswerMarkdown(
  content: string,
  resolvedDocuments: ResolvedDocumentMap,
  risLinkMetadata?: RISLinkMetadata,
): string {
  return linkRisReferencesMarkdown(
    linkResolvedDocumentMarkdown(content, resolvedDocuments),
    risTextReferences(risLinkMetadata),
  );
}

/**
 * Antworttext einer Recherchenachricht. Dargestellt wird er wie in der
 * Vergleichskarte: derselbe Markdown-Weg, dieselbe Auflösung der Adressen auf
 * Dokumente des eigenen Korpus. Die frühere schmale HTML-Formatierung des
 * Chats kannte weder Zitatblöcke noch eingerückte oder mit Strich
 * geschriebene Aufzählungen und beschriftete jeden RIS-Verweis mit "[RIS]";
 * das traf beide Recherchewege gleichermaßen.
 */
export const ResearchAnswerBody: React.FC<{
  content: string;
  resolvedDocuments: ResolvedDocumentMap;
  risLinkMetadata?: RISLinkMetadata;
  className?: string;
  onOpenRisSource: (url: string) => void;
  onOpenDocumentSource: (documentId: string) => void;
}> = ({ content, resolvedDocuments, risLinkMetadata, className = '', onOpenRisSource, onOpenDocumentSource }) => {
  const markdown = useMemo(
    () => preparedAnswerMarkdown(content, resolvedDocuments, risLinkMetadata),
    [content, resolvedDocuments, risLinkMetadata],
  );
  return (
    <LegalMarkdown
      content={markdown}
      // "research-chat-surface" färbt die geerbten Farben der geteilten
      // Darstellung im dunklen Modus um (siehe index.css); die Grundfarbe des
      // Textes steht als Tailwind-Variante direkt an der Wurzel.
      className={`research-chat-surface dark:text-gray-100 ${className}`.trimEnd()}
      onOpenRisSource={onOpenRisSource}
      onOpenDocumentSource={onOpenDocumentSource}
    />
  );
};

/**
 * Quellenblock einer Recherchenachricht. Die Darstellung stammt aus der
 * Vergleichskarte: jede Quelle nennt ihren Titel oder eine abgeleitete
 * Beschriftung, die Adresse steht nur hinter dem Verweis.
 */
export const ResearchSourceList: React.FC<{
  sources: ResearchSource[];
  answer: string;
  resolvedDocuments: ResolvedDocumentMap;
  risLinkMetadata?: RISLinkMetadata;
  onOpenSource: (target: ViewerSourceTarget) => void;
}> = ({ sources, answer, resolvedDocuments, risLinkMetadata, onOpenSource }) => {
  // Eine Quelle, die nur als Adresse vorliegt, trägt die Beschriftung, unter
  // der die Antwort sie nennt; erst danach greift die Ableitung aus der Adresse.
  // Gelesen wird derselbe vorbereitete Text, den der Antwortkörper darstellt.
  const labels = useMemo(
    () => answerLinkLabels(preparedAnswerMarkdown(answer, resolvedDocuments, risLinkMetadata)),
    [answer, resolvedDocuments, risLinkMetadata],
  );
  if (sources.length === 0) return null;
  return (
    <div className="research-chat-surface mt-4 border-t border-warm-200 pt-3 dark:border-gray-700">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted dark:text-gray-400">
        Quellen
      </p>
      <ul className="mt-2 space-y-1.5 text-xs text-muted dark:text-gray-300">
        {sources.map((source, sourceIndex) => (
          <SourceItem
            key={sourceIndex}
            source={source}
            index={sourceIndex}
            resolvedDocuments={resolvedDocuments}
            answerLabels={labels}
            onOpenSource={onOpenSource}
          />
        ))}
      </ul>
    </div>
  );
};

/**
 * Alle Adressen einer Nachricht, die auf ein Dokument des eigenen Korpus
 * zeigen können: angeführte Quellen ohne Dokumentkennung, geprüfte Quellen
 * ohne Dokumentkennung und die Adressen im Antworttext selbst.
 */
function documentUrlCandidates(
  message: Pick<RISMessage, 'content' | 'sources' | 'verifiedSources'>,
): Array<string | null> {
  const fromSources = (message.sources || []).map(source => {
    const target = resolveViewerSourceTarget(source);
    return target.kind === 'external' ? target.url : null;
  });
  const fromVerified = (message.verifiedSources || []).map(source => (
    source.documentId ? null : source.risLink || null
  ));
  return [...fromSources, ...fromVerified, ...resolvableUrlsInText(message.content)];
}

/**
 * Eine Antwort aus dem Testbau: Bahn B fragt Gemini, Bahn C das lokale Modell.
 * Beide nehmen denselben Weg durch die Darstellung -- nur das Etikett über der
 * Antwort unterscheidet sie --, und beide führen ihre Fundstellen ohne die
 * Meldungen des eigenen Rechercheweges.
 */
export function isStagingLaneMessage<TMessage extends Pick<RISMessage, 'researchMetadata'>>(
  message: TMessage,
): message is TMessage & { researchMetadata: ResearchMetadata } {
  const lane = message.researchMetadata?.lane;
  return lane === 'B' || lane === 'C' || lane === 'D';
}

/**
 * Das Etikett über einer Antwort der Bahn C. Ohne es sähe der Prüfer der
 * fertigen Antwort nicht an, welches Modell sie verfasst hat.
 */
export const StagingLaneLabel: React.FC<{
  message: Pick<RISMessage, 'researchMetadata'>;
  /** Nur ein Entwicklerkonto sieht Etiketten; der Chat bleibt sonst blank. */
  developer: boolean;
}> = ({ message, developer }) => (
  developer && (message.researchMetadata?.lane === 'C' || message.researchMetadata?.lane === 'D') ? (
    <p className="mb-3 inline-block border border-warm-200 bg-warm-100 px-2 py-1 text-xs font-semibold text-muted dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
      {message.researchMetadata.lane === 'D'
        ? `Bahn D, Cortecs${message.researchMetadata.modelPreset ? `: ${message.researchMetadata.modelPreset}` : ''}`
        : 'Bahn C, lokales Modell'}
    </p>
  ) : null
);

/** Ein Eintrag der Modellwahl des Chats. */
export interface ChatModelOption {
  /** Stabiler Wert des Auswahlfeldes, etwa 'B' oder 'D:glm-5.3-flash'. */
  value: string;
  label: string;
  selection: StagingLaneSelection;
}

/**
 * Die Modelle, die der Chat zur Wahl stellt, in fester Reihenfolge: zuerst
 * Gemini, dann das lokale Modell, dann je ein Cortecs-Modell. Bahn C hängt am
 * Testbau, Bahn D allein an der Freigabe des Servers -- die Presetliste kommt
 * von ihm, nie aus dem Browser. Ohne Freigabe fehlt die Bahn ganz; sie wäre
 * nicht wählbar.
 */
export function chatModelOptions(laneDPresets: readonly string[] = []): ChatModelOption[] {
  const options: ChatModelOption[] = [
    { value: 'B', label: 'Gemini (Bahn B)', selection: { variant: 'B' } },
  ];
  if (STAGING_LANE_C_ENABLED) {
    options.push({ value: 'C', label: 'Lokales Modell (Bahn C)', selection: { variant: 'C' } });
  }
  for (const preset of laneDPresets) {
    options.push({
      value: `D:${preset}`,
      label: `Cortecs: ${preset}`,
      selection: { variant: 'D', preset },
    });
  }
  return options;
}

/** Der Wert des Auswahlfeldes zu einer Wahl. */
export function chatModelValue(selection: StagingLaneSelection): string {
  return selection.variant === 'D' && selection.preset ? `D:${selection.preset}` : selection.variant;
}

/**
 * Die Wahl zu einem Wert des Auswahlfeldes. Ein Wert, den die Liste des
 * Servers nicht kennt, bleibt ungültig: niemals unbemerkt ein anderes Modell
 * anfordern, wenn sich die Freigabe inzwischen geändert hat.
 */
export function chatModelSelection(
  value: string,
  laneDPresets: readonly string[] = [],
): StagingLaneSelection | undefined {
  return chatModelOptions(laneDPresets).find(option => option.value === value)?.selection;
}

/**
 * Die Wahl, mit der eine Frage tatsächlich läuft. Sie gilt nur, solange die
 * Auswahl überhaupt dasteht und das gewählte Modell noch freigegeben ist;
 * sonst fragt der Chat Gemini.
 */
export function pinnedChatModelSelection(
  selection: StagingLaneSelection,
  developer: boolean,
  laneDPresets: readonly string[] = [],
): StagingLaneSelection {
  if (!showsLaneVariantSwitch(developer, laneDPresets)) return DEFAULT_STAGING_LANE_SELECTION;
  return chatModelSelection(chatModelValue(selection), laneDPresets) ?? DEFAULT_STAGING_LANE_SELECTION;
}

/**
 * Die Wahl eines gescheiterten Laufs für den zweiten Versuch. Ohne gültiges
 * Modell bleibt es bei Gemini: eine Bahn D ohne Modell weist der Server ab.
 */
export function retriedLaneSelection(
  laneVariant: StagingLaneVariant | undefined,
  modelPreset: string | undefined,
): StagingLaneSelection {
  if (laneVariant === 'C') return { variant: 'C' };
  if (laneVariant === 'D' && modelPreset) return { variant: 'D', preset: modelPreset };
  return DEFAULT_STAGING_LANE_SELECTION;
}

/** Bahn und Modell in Worten, für das Etikett über einer Antwort. */
export function chatModelLabel(selection: StagingLaneSelection): string {
  if (selection.variant === 'C') return 'Bahn C, lokales Modell';
  if (selection.variant === 'D') {
    return selection.preset ? `Bahn D, Cortecs: ${selection.preset}` : 'Bahn D, Cortecs';
  }
  return 'Bahn B, Gemini';
}

/**
 * Ob der Chat Anhänge annimmt. Sie tragen den Fallkontext der Frage, und der
 * erreicht die Recherche über die Bahn B, auf der seit der Abschaltung der
 * Bahn A (Owner-Entscheidung 19.10.2026) jede Frage läuft. Allein die Bahn C
 * bleibt außen vor: das lokale Modell hat ein knappes Fenster, das ein
 * Fallkontext dieser Größe der Recherche selbst wegnähme. Gemini und die
 * Cortecs-Modelle nehmen ihn.
 */
export function showsChatUpload(
  developer: boolean,
  laneVariant: StagingLaneVariant = 'B',
  laneDPresets: readonly string[] = [],
): boolean {
  return !(showsLaneVariantSwitch(developer, laneDPresets) && laneVariant === 'C');
}

/**
 * Die Dateiarten, die der Dateiwähler anbietet. Die Cortecs-Modelle der
 * Bahn D lesen keine Dateien; dort bietet er nur DOCX, TXT und MD an.
 */
export function chatAttachmentAccept(laneVariant: StagingLaneVariant = 'B'): { accept: string; label: string } {
  return laneVariant === 'D'
    ? { accept: CHAT_ATTACHMENT_TEXT_ACCEPT, label: CHAT_ATTACHMENT_TEXT_TYPES_LABEL }
    : { accept: CHAT_ATTACHMENT_ACCEPT, label: CHAT_ATTACHMENT_TYPES_LABEL };
}

/**
 * Ob die Wahl des Modells sichtbar ist: nur im Testbau der Bahn B, nur für ein
 * Entwicklerkonto -- und nur, wenn es überhaupt etwas zu wählen gibt.
 */
export function showsLaneVariantSwitch(
  developer: boolean,
  laneDPresets: readonly string[] = [],
): boolean {
  return STAGING_LANE_B_ENABLED && developer && chatModelOptions(laneDPresets).length > 1;
}

/**
 * Der Einstieg in den Vergleich: nur ein Entwicklerkonto, dem der Server den
 * Vergleich auch tatsächlich meldet. Jedes andere Konto sieht im Kopf des
 * Chats keine Abzweigung.
 */
export function showsComparisonEntry(developer: boolean, comparisonAvailable: boolean): boolean {
  return developer && comparisonAvailable;
}

/**
 * Die Wahl des Modells innerhalb der Bahn B: Gemini, das lokale Modell oder
 * eines der freigegebenen Cortecs-Modelle. Sie gilt ab der nächsten Recherche;
 * ein laufender Lauf behält sein Modell. Ein Modell, das der Server nicht
 * mehr nennt, steht weiterhin als abgewählter Eintrag da, statt still durch
 * ein anderes ersetzt zu werden.
 */
export const StagingLaneModelSelect: React.FC<{
  selection: StagingLaneSelection;
  laneDPresets?: readonly string[];
  disabled: boolean;
  onSelect: (selection: StagingLaneSelection) => void;
}> = ({ selection, laneDPresets = [], disabled, onSelect }) => {
  const options = chatModelOptions(laneDPresets);
  const value = chatModelValue(selection);
  return (
    // DR-13 UI-06: schmal steht die Wahl in einer eigenen Zeile unter Schalter und A/B-Vergleich und wird nie breiter als sie.
    <label
      className="order-last flex min-w-0 max-w-full items-center gap-2 border border-warm-200 bg-warm-100 px-3 py-2 text-xs font-semibold text-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 sm:order-none"
      htmlFor="chat-model-select"
      title="Gilt ab der nächsten Recherche und bleibt während dieses Laufs unverändert."
    >
      Modell
      <select
        id="chat-model-select"
        value={value}
        disabled={disabled}
        onChange={event => {
          const next = chatModelSelection(event.target.value, laneDPresets);
          if (next) onSelect(next);
        }}
        className="min-w-0 border border-warm-200 bg-white px-2 py-1 text-xs font-normal text-primary focus:border-accent focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
      >
        {!options.some(option => option.value === value) && (
          <option value={value} disabled>
            {`${chatModelLabel(selection)} (nicht verfügbar)`}
          </option>
        )}
        {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
};

/**
 * Bahn und Modell des laufenden Zugs, über der entstehenden Antwort. Solange
 * der Server seine Bahn nicht gemeldet hat, steht hier die angeforderte Wahl
 * -- und sie sagt ausdrücklich, dass sie nur der Wunsch der Anfrage ist.
 * Sobald der Server seine Bahn meldet, tritt das Etikett der Antwort selbst
 * an ihre Stelle (StagingLaneLabel), damit nicht zwei Zeilen dasselbe sagen.
 * Nur ein Entwicklerkonto sieht sie, und nur für die Bahnen mit eigenem
 * Modell -- ein gewöhnlicher Gemini-Lauf bleibt so blank wie bisher.
 */
export const StagingLaneRunLabel: React.FC<{
  requested: StagingLaneSelection;
  confirmed?: StagingLaneVariant;
  developer: boolean;
}> = ({ requested, confirmed, developer }) => {
  if (!developer || confirmed || requested.variant === 'B') return null;
  return (
    <p className="mb-3 inline-block border border-warm-200 bg-warm-100 px-2 py-1 text-xs font-semibold text-muted dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
      {`${chatModelLabel(requested)} (angefordert)`}
    </p>
  );
};

/**
 * Eine Klartext-Antwort einer Staging-Bahn: der Klartext steht als Nachricht,
 * die ausführliche Schicht darunter. Ihre Quellen führt der Fußbereich des
 * Gesprächs, nicht mehr ein Block unter der Antwort.
 */
export function isKlartextAnswer(
  message: Pick<RISMessage, 'researchMetadata'>,
): boolean {
  return isStagingLaneMessage(message)
    && message.researchMetadata?.answerProfile === 'klartext';
}

/** Blanke Adresse im Text einer Antwort. */
const URL_IN_ANSWER = /https?:\/\/[^\s<>"'\])]+/g;

/**
 * Die Adressen, die im Text einer Antwort stehen, in der Schreibweise, unter
 * der Antwort und Quellenliste dieselbe Adresse meinen.
 */
function answerUrlKeys(content: string): Set<string> {
  const keys = new Set<string>();
  for (const match of content.matchAll(URL_IN_ANSWER)) {
    const key = sourceLabelKey(match[0].replace(/[.,;:!?]+$/, ''));
    if (key) keys.add(key);
  }
  return keys;
}

/**
 * Beschriftung einer beanstandeten Adresse: die Fundstelle, unter der die
 * Antwort sie nennt ("OGH 9 ObA 1/24x"), sonst die aus der Adresse abgeleitete
 * Beschriftung. Die rohe Adresse selbst steht nie als Beschriftung da.
 */
export function objectionClaimLabel(url: string, answerLabels: Map<string, string>): string {
  const key = sourceLabelKey(url);
  const named = key === null ? undefined : answerLabels.get(key);
  if (named) return named;
  if (isOfficialRisUrl(url)) return risReferenceLabel(url);
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/**
 * Die beanstandete Stelle eines Einwands: eine reine Adresse, oder eine
 * Fundstelle samt Adresse ("OGH 8 ObA 1/02h (https://…)"). Die Adresse wird
 * zum Verweis, die Fundstelle um sie herum zu seiner Beschriftung; ohne eine
 * steht die abgeleitete Beschriftung da. Ein Textauszug bleibt Auszug.
 */
function objectionClaim(claimText: string, answerLabels: Map<string, string>): { url: string | null; label: string } {
  const direct = safeHttpUrl(claimText);
  if (direct !== null) return { url: direct, label: objectionClaimLabel(direct, answerLabels) };
  const match = new RegExp(URL_IN_ANSWER.source).exec(claimText);
  const url = match === null ? null : safeHttpUrl(match[0].replace(/[.,;:!?]+$/, ''));
  if (match === null || url === null) return { url: null, label: claimText };
  const around = [
    claimText.slice(0, match.index).replace(/[\s(]+$/, ''),
    claimText.slice(match.index + match[0].length).replace(/^[\s).,;:]+/, ''),
  ].filter(part => part.trim() !== '').join(' ').trim();
  return { url, label: around || objectionClaimLabel(url, answerLabels) };
}

/**
 * Die Einwände einer Antwort, verteilt auf ihre beiden Schichten. Bei der
 * Klartext-Antwort prüft die Bahn B beide Schichten zusammen. In die
 * eingeklappte Schicht wandert allein, was dort nachweislich hingehört: eine
 * beanstandete Adresse, die die ausführliche Schicht führt und der Klartext
 * nicht. Jeder andere Einwand bleibt unter der Antwort -- ein Textauszug ist
 * beschnitten und träfe den Klartext auch dann nicht wörtlich, wenn er ihn
 * meint, und eine nirgends auffindbare Adresse verschwände sonst aus dem Blick.
 */
export function objectionLayers(
  message: Pick<RISMessage, 'content' | 'reviewAnnotation' | 'researchMetadata'>,
): { answer: ReviewObjection[]; detailed: ReviewObjection[] } {
  const review = message.reviewAnnotation || message.researchMetadata?.reviewAnnotation;
  const objections = review?.status === 'objections' ? review.objections : [];
  const detailedAnswer = message.researchMetadata?.detailedAnswer;
  if (!isKlartextAnswer(message) || !detailedAnswer) {
    return { answer: [...objections], detailed: [] };
  }
  const klartextKeys = answerUrlKeys(message.content || '');
  const detailedKeys = answerUrlKeys(detailedAnswer);
  const answer: ReviewObjection[] = [];
  const detailed: ReviewObjection[] = [];
  for (const objection of objections) {
    const url = safeHttpUrl(objection.claimText);
    const key = url === null ? null : sourceLabelKey(url);
    const onlyInDetailed = key !== null && detailedKeys.has(key) && !klartextKeys.has(key);
    (onlyInDetailed ? detailed : answer).push(objection);
  }
  return { answer, detailed };
}

/**
 * Ob unter der Antwort überhaupt ein Prüfblock steht. Er trägt dreierlei: das
 * Abzeichen der Prüfung -- bei der Klartext-Antwort trägt es der Kopf der
 * ausführlichen Schicht --, die Einwände gegen die Antwort selbst und die
 * geprüften Quellen. Bleibt nichts davon, stünde unter der Antwort ein bloßer
 * Trennstrich; die Einwände der ausführlichen Schicht stehen unter deren Kopf
 * und halten den Block hier nicht offen.
 */
export function messageShowsReviewBlock(
  message: Pick<RISMessage, 'content' | 'reviewAnnotation' | 'researchMetadata' | 'verifiedSources'>,
): boolean {
  const review = message.reviewAnnotation || message.researchMetadata?.reviewAnnotation;
  const klartext = isKlartextAnswer(message);
  if (!klartext && (message.verifiedSources || []).length > 0) return true;
  if (!klartext && review !== undefined) return true;
  return review?.status === 'objections' && objectionLayers(message).answer.length > 0;
}

/**
 * Die Einwände der anwaltlichen Prüfung. Die beanstandete Stelle steht so da,
 * wie die Antwort sie führt: eine Adresse als benannter Verweis, der denselben
 * Weg nimmt wie jede andere Fundstelle, ein Textauszug als Auszug.
 */
export const ReviewObjectionList: React.FC<{
  objections: readonly ReviewObjection[];
  answerLabels: Map<string, string>;
  onOpenSource: (target: ViewerSourceTarget) => void;
}> = ({ objections, answerLabels, onOpenSource }) => {
  if (objections.length === 0) return null;
  return (
    <ul className="mt-3 space-y-2 text-sm text-primary [overflow-wrap:anywhere] dark:text-gray-200">
      {objections.map((objection, objectionIndex) => {
        const { url, label } = objectionClaim(objection.claimText, answerLabels);
        const target = url === null ? { kind: 'none' as const } : resolveViewerSourceTarget(url);
        // Eine fremde Adresse wird nur verschlüsselt und ohne Zugangsdaten zum
        // Verweis; sonst steht ihre Beschriftung als Text da.
        const externalHref = target.kind === 'external' ? externalLinkUrl(target.url) : null;
        return (
          <li key={`${objection.claimText}-${objectionIndex}`} className="border-l-2 border-amber-400 pl-3">
            <p className="font-semibold">
              {target.kind === 'ris' ? (
                <a
                  href={target.url}
                  data-ris-preview="true"
                  onClick={event => { event.preventDefault(); onOpenSource(target); }}
                  rel="noopener noreferrer"
                  className="text-primary underline decoration-accent/50 underline-offset-2 hover:text-accent-dark dark:text-gray-200"
                >{label}</a>
              ) : target.kind === 'document' ? (
                <button
                  type="button"
                  data-document-preview={target.documentId}
                  onClick={() => onOpenSource(target)}
                  className="text-left text-primary underline decoration-accent/50 underline-offset-2 hover:text-accent-dark dark:text-gray-200"
                >{label}</button>
              ) : externalHref !== null ? (
                <a
                  href={externalHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline decoration-accent/50 underline-offset-2 hover:text-accent-dark dark:text-gray-200"
                >{label}</a>
              ) : (
                label
              )}
            </p>
            <p className="mt-0.5 text-xs font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300">
              {reviewObjectionLabel(objection)}
            </p>
            <p className="mt-1 text-muted dark:text-gray-400">{objection.reasoning}</p>
          </li>
        );
      })}
    </ul>
  );
};

/**
 * Die Einwände einer Nachricht in einer ihrer beiden Schichten. Aufteilung und
 * Beschriftungen entstehen hier und nicht bei jedem Durchlauf der
 * Nachrichtenliste: beide lesen den ganzen Antworttext, und der ist bei einer
 * Recherche lang.
 */
export const MessageReviewObjections: React.FC<{
  message: Pick<RISMessage, 'content' | 'reviewAnnotation' | 'researchMetadata'>;
  layer: 'answer' | 'detailed';
  resolvedDocuments: ResolvedDocumentMap;
  onOpenSource: (target: ViewerSourceTarget) => void;
}> = ({ message, layer, resolvedDocuments, onOpenSource }) => {
  const content = message.content || '';
  const detailedAnswer = message.researchMetadata?.detailedAnswer;
  const review = message.reviewAnnotation || message.researchMetadata?.reviewAnnotation;
  const objections = review?.status === 'objections' ? review.objections : undefined;
  const layered = useMemo(
    () => objectionLayers(message)[layer],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [content, detailedAnswer, objections, layer],
  );
  // Beide Schichten tragen zu den Beschriftungen bei: ein Einwand gegen die
  // ausführliche Schicht nennt die dort geführte Fundstelle.
  const answerLabels = useMemo(
    () => answerLinkLabels(preparedAnswerMarkdown(
      [content, detailedAnswer || ''].filter(Boolean).join('\n\n'),
      resolvedDocuments,
    )),
    [content, detailedAnswer, resolvedDocuments],
  );
  return (
    <ReviewObjectionList
      objections={layered}
      answerLabels={answerLabels}
      onOpenSource={onOpenSource}
    />
  );
};

/**
 * Die Nachrichtenliste des Quellen-Fußbereichs. Zwischen dem Ende der
 * Komposition und dem Eintrag der Nachricht in den Verlauf steht die fertige
 * Antwort nur als laufender Text da; ihr eigener Quellenblock entfällt bei der
 * Klartext-Antwort bereits, der Fußbereich kennt sie aber noch nicht. Solange
 * die gespeicherte Fassung fehlt, tritt die laufende an ihre Stelle; sobald sie
 * da ist, fällt die laufende weg und nichts steht doppelt.
 */
export function sourcesFooterMessages(
  messages: readonly RISMessage[],
  live: LiveResearchState | null,
  persistedAnswer: RISMessage | undefined,
  researchMetadata: ResearchMetadata | undefined,
): RISMessage[] {
  if (!live?.answer || persistedAnswer) return [...messages];
  return [...messages, {
    role: 'assistant',
    content: live.answer,
    timestamp: new Date(),
    sources: live.sources,
    verifiedSources: live.verifiedSources,
    reviewAnnotation: live.reviewAnnotation,
    researchMetadata,
  }];
}

/**
 * Der Text, den die Schaltfläche "Kopieren" in die Zwischenablage legt. Bei
 * einer Klartext-Antwort hängt die ausführliche Schicht hinten an: wer die
 * Antwort in einen Aktenvermerk einfügt, braucht den vollständigen A-G-Teil
 * und nicht nur die kurze Fassung, die auf dem Schirm stand.
 */
export function copyTextOfMessage(
  message: Pick<RISMessage, 'content' | 'researchMetadata'>,
): string {
  const content = message.content || '';
  const detailed = isKlartextAnswer(message) ? message.researchMetadata?.detailedAnswer : undefined;
  return detailed ? `${content}\n\nAusführliche Analyse\n\n${detailed}` : content;
}

/**
 * Der Quellen-Fußbereich gehört zur Klartext-Ansicht: er tritt an die Stelle
 * der Quellenblöcke unter den Antworten. Solange eine Sitzung keine
 * Klartext-Antwort führt, stehen diese Blöcke noch da und der Fußbereich
 * zeigte dieselben Quellen ein zweites Mal. Eine einzige Bedingung, damit der
 * Eigentümer sie später auf "immer" stellen kann.
 */
export function sessionShowsSourcesFooter(
  messages: readonly Pick<RISMessage, 'researchMetadata'>[],
): boolean {
  return messages.some(isKlartextAnswer);
}

const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Find top-level section (### A. ... style) range by letter and title
const findTopLevelSectionRange = (
  content: string,
  letter: 'E' | 'F',
  title: string
): { start: number; end: number } | null => {
  const headerRegex = new RegExp(`^###\\s+${letter}\\.\\s+${escapeRegExp(title)}\\s*$`, 'm');
  const match = content.match(headerRegex);
  if (!match) return null;
  const start = match.index ?? 0;
  const afterHeaderOffset = start + match[0].length;
  // Next top-level header like "### X. ..." (single capital letter + dot)
  const nextHeaderRegex = /^###\s+[A-Z]\.\s+/m;
  const rest = content.slice(afterHeaderOffset);
  const nextIdxInRest = rest.search(nextHeaderRegex);
  const end = nextIdxInRest >= 0 ? afterHeaderOffset + nextIdxInRest : content.length;
  return { start, end };
};

// Split a section by case separators (---) and return preview (first 3 entries) and the remaining
const splitSectionPreview = (
  sectionContent: string,
  maxEntries: number = 3
): { preview: string; rest: string } => {
  const sepRegex = /\n-{3,}\n+/g;
  let count = 0;
  let match: RegExpExecArray | null;
  let cutoff = -1;
  while ((match = sepRegex.exec(sectionContent)) !== null) {
    count++;
    if (count === maxEntries) {
      cutoff = match.index + match[0].length;
      break;
    }
  }
  if (cutoff === -1) {
    // Less than maxEntries separators -> no preview split
    return { preview: sectionContent, rest: '' };
  }
  return {
    preview: sectionContent.slice(0, cutoff),
    rest: sectionContent.slice(cutoff)
  };
};

/**
 * Antworttext mit der Klappe der Abschnitte E und F. Zusammengeklappt steht
 * nur der Anfang eines Abschnitts da, der Rest erscheint auf Wunsch. Jeder
 * Textteil nimmt dieselbe Darstellung wie jede andere Recherchenachricht.
 * Geschnitten wird ausschließlich an Überschriften der obersten Ebene und an
 * den Trennlinien zwischen den Einträgen; beide beenden in der Darstellung
 * jeden offenen Block, sodass kein Zitatblock und keine Aufzählung
 * zerschnitten wird.
 */
export const CollapsibleAnswerBody: React.FC<{
  content: string;
  resolvedDocuments: ResolvedDocumentMap;
  risLinkMetadata?: RISLinkMetadata;
  expanded: { E: boolean; F: boolean };
  onToggleSection: (section: 'E' | 'F') => void;
  onOpenRisSource: (url: string) => void;
  onOpenDocumentSource: (documentId: string) => void;
}> = ({
  content,
  resolvedDocuments,
  risLinkMetadata,
  expanded,
  onToggleSection,
  onOpenRisSource,
  onOpenDocumentSource,
}) => {
  const piece = (key: string, text: string, className?: string) => (
    <ResearchAnswerBody
      key={key}
      content={text}
      resolvedDocuments={resolvedDocuments}
      risLinkMetadata={risLinkMetadata}
      className={className}
      onOpenRisSource={onOpenRisSource}
      onOpenDocumentSource={onOpenDocumentSource}
    />
  );

  // Locate sections E and F
  const eRange = findTopLevelSectionRange(content, 'E', 'Relevante Gerichtsentscheidungen');
  const fRange = findTopLevelSectionRange(content, 'F', 'Relevante Rechtsprechung');

  // Ohne diese Abschnitte gibt es nichts zu klappen: die Antwort steht ganz da.
  if (!eRange && !fRange) return piece('answer', content);

  // Determine ordered sections by start index
  const sections: Array<{
    id: 'E' | 'F';
    range: { start: number; end: number };
    title: string;
  }> = [];
  if (eRange) sections.push({ id: 'E', range: eRange, title: 'Relevante Gerichtsentscheidungen' });
  if (fRange) sections.push({ id: 'F', range: fRange, title: 'Relevante Rechtsprechung' });
  sections.sort((a, b) => a.range.start - b.range.start);

  const parts: React.ReactNode[] = [];
  let cursor = 0;

  sections.forEach((sec, idx) => {
    const { start, end } = sec.range;
    // Add content before this section
    if (cursor < start) {
      const before = content.slice(cursor, start);
      if (before.trim()) parts.push(piece(`before-${idx}`, before));
    }

    const sectionText = content.slice(start, end);
    const { preview, rest } = splitSectionPreview(sectionText, 3);

    // Always render preview
    parts.push(piece(`sec-${sec.id}-preview-${idx}`, preview));

    // Render toggle and (optionally) rest
    if (rest && rest.trim()) {
      const isExpanded = expanded[sec.id];
      parts.push(
        <div key={`sec-${sec.id}-toggle-${idx}`} className="mt-0 flex justify-center">
          <button
            onClick={() => onToggleSection(sec.id)}
            className="text-sm text-primary hover:text-accent-dark underline"
          >
            {isExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
          </button>
        </div>
      );

      if (isExpanded) parts.push(piece(`sec-${sec.id}-rest-${idx}`, rest, 'mt-2'));
    }

    cursor = end;
  });

  // Add any trailing content after the last section
  if (cursor < content.length) {
    const tail = content.slice(cursor);
    if (tail.trim()) parts.push(piece('after-tail', tail));
  }

  return (
    <div className="max-w-none break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
      {parts}
    </div>
  );
};

const SCHMAL_EINGABE = '(max-width: 639px)';

const RISAssistant: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isDeveloper, checkAuth } = useAuth();
  const brand = useBrand();
  const riSearchContext = useRISearch(); // Global context for background search
  const { isSessionSearching, cancelSearch, getLiveSearch, getSessionResearch, acknowledgeSessionResearch, reportRunSeen } = riSearchContext;
  const { detailRuns, startDetailRun, resumeDetailRun, isDetailRunning } = riSearchContext;
  const { openViewer, openDocumentViewer } = useRISViewer(); // In-app RIS document viewer
  const { currentSessionId, setCurrentSessionId, triggerSidebarRefresh, pendingSessionLoad, clearPendingSessionLoad } = useAppLayout();

  // Always use tooluse mode (RISearch 2.0) - mode selector removed
  const selectedMode: RISMode = 'tooluse';
  const [messages, setMessages] = useState<RISMessage[]>([]);
  /** Bahn A: je Frage ohne Antwort der Endzustand ihres letzten Laufs, wie ihn der Server beim Laden des Chats nennt. */
  const [endedRuns, setEndedRuns] = useState<Record<string, ResearchRunEnd>>({});
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Profil-Nachbau F-06 (BAU-HILFE.md A-10, PF-316; TEIL-3 SP3-01 „Weitere Einstiege“): der Fehlercode zum Link unter dem
  // Fehler einer Recherche; jedes andere setError nimmt ihn wieder weg.
  const [meldeFehlercode, setMeldeFehlercode] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [copiedId, setCopiedId] = useState<string>('');
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  // Profil-Nachbau F-09 (BAU-PRAEFERENZEN.md 1.4): „Chat-Modus“ ist „Mit Enter senden“ am Konto; ohne Wahl am Konto die frühere
  // Wahl im Browser, sonst aus (F-P3). Ändert sich der Wert am Konto (Seite „Präferenzen“), zeigt das Feld ihn ebenfalls.
  const kontoSendetMitEnter = user?.account?.sendWithEnter;
  const [chatMode, setChatMode] = useState(() => mitEnterSenden(kontoSendetMitEnter, alterChatModus()));
  useEffect(() => { if (typeof kontoSendetMitEnter === 'boolean') setChatMode(kontoSendetMitEnter); }, [kontoSendetMitEnter]);
  // Auf schmalen Schirmen passt nur der kurze Platzhalter in das Eingabefeld.
  const [schmal, setSchmal] = useState(() => window.matchMedia(SCHMAL_EINGABE).matches);
  useEffect(() => {
    const abfrage = window.matchMedia(SCHMAL_EINGABE);
    const wechsel = () => setSchmal(abfrage.matches);
    abfrage.addEventListener('change', wechsel);
    return () => abfrage.removeEventListener('change', wechsel);
  }, []);
  const [comparisonAvailable, setComparisonAvailable] = useState(false);
  const websucheAn = useWebsucheAn();
  // Die freigegebenen Cortecs-Modelle nennt allein der Server; ohne seine
  // Antwort steht die Bahn D gar nicht erst zur Wahl.
  const [laneDPresets, setLaneDPresets] = useState<string[]>([]);
  /**
   * Die Modellwahl je Chat: eine Folgefrage läuft mit dem Modell der vorigen,
   * bis der Prüfer ein anderes wählt. Sie gilt nur in dieser Sitzung des
   * Browsers und wird nirgends gespeichert.
   */
  const [laneSelections, setLaneSelections] = useState<Record<string, StagingLaneSelection>>({});
  /** Der Schlüssel der Wahl: der geladene Chat -- oder der noch namenlose neue. */
  const laneSelectionKey = sessionId || 'neuer-chat';
  const laneSelection = laneSelections[laneSelectionKey] ?? DEFAULT_STAGING_LANE_SELECTION;
  const rememberLaneSelection = (key: string, selection: StagingLaneSelection): void => {
    setLaneSelections(previous => ({ ...previous, [key]: selection }));
  };
  // Die Anhänge der nächsten Frage. Sie liegen bereits am Server; hier steht
  // nur, was der Leser über sie sieht.
  const [attachments, setAttachments] = useState<ChatAttachmentSummary[]>([]);
  // AG-6: die Dokumente im Kontext des Chats; sie gehen mit jeder Frage erneut mit.
  const [contextDocuments, setContextDocuments] = useState<ChatContextDocument[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const reuploadInputRef = useRef<HTMLInputElement>(null);
  const reuploadTargetRef = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const selectionRef = useRef(new LatestSelection());
  const consumedStartRef = useRef<string | null>(null);
  // Profil-Nachbau F-20.3 (BAU-VORLAGEN.md 5.2, V-6): nach „Vorlage einfügen“ markiert das Feld beim nächsten Rendern den ersten
  // Platzhalter, ohne Platzhalter steht die Marke am Textende.
  const [markierung, setMarkierung] = useState<{ start: number; ende: number; nr: number } | null>(null);
  useEffect(() => {
    if (!markierung) return;
    const feld = textareaRef.current;
    feld?.focus();
    feld?.setSelectionRange(markierung.start, markierung.ende);
    autoResizeTextarea();
  }, [markierung]);
  const vorlageEinfuegen = (text: string) => {
    setInputValue(text);
    const platzhalter = ersterPlatzhalter(text) ?? { start: text.length, ende: text.length };
    setMarkierung(alt => ({ ...platzhalter, nr: (alt?.nr ?? 0) + 1 }));
  };

  // The experimental entry stays absent from normal production builds and requires server capability.
  // Ein gewöhnliches Konto fragt gar nicht erst nach: der Vergleich ist für
  // es weder sichtbar noch erreichbar. Dieselbe Auskunft nennt auch die
  // freigegebenen Cortecs-Modelle, die der Chat zur Wahl stellt.
  useEffect(() => {
    if (import.meta.env.VITE_LOCAL_RESEARCH_COMPARE !== 'true') return;
    if (!isDeveloper) {
      setComparisonAvailable(false);
      setLaneDPresets([]);
      return;
    }
    const controller = new AbortController();
    getResearchComparisonCapabilities(controller.signal)
      .then(capabilities => {
        setComparisonAvailable(capabilities.enabled);
        setLaneDPresets(capabilities.models.laneD.enabled ? [...capabilities.models.laneD.presets] : []);
      })
      .catch(() => {
        setComparisonAvailable(false);
        setLaneDPresets([]);
      });
    return () => controller.abort();
  }, [isDeveloper]);

  // Ref to track current sessionId for stale closure protection
  const sessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Ref to guard isLoading: only the active search's handleSubmit can clear it
  const activeSubmitIdRef = useRef<string | null>(null);

  // Handle pending session load from sidebar
  useEffect(() => {
    if (pendingSessionLoad) {
      handleSessionSelect(pendingSessionLoad.sessionId);
      clearPendingSessionLoad();
    }
  }, [pendingSessionLoad]);

  // Initialize session ID on mount if not already set
  useEffect(() => {
    const initSession = async () => {
      if (!sessionId && !currentSessionId) {
        const newSessionId = await webChatHistoryService.generateSessionId('ris', user?.id);
        setSessionId(newSessionId);
        setCurrentSessionId(newSessionId);
        console.log(`[RIS] Initialized new session on mount: ${newSessionId}`);
      } else if (currentSessionId && currentSessionId !== sessionId) {
        if (pendingSessionLoad?.sessionId !== currentSessionId) {
          selectionRef.current.issue(currentSessionId);
          console.log(`[RIS] New session detected, clearing state: ${currentSessionId}`);
          setMessages([]);
          setEndedRuns({});
          setIsLoading(false);
          setIsLoadingSession(false);
          activeSubmitIdRef.current = null; // Invalidate old handleSubmit's finally block
          setError(null);
          setMeldeFehlercode(null);
          setAttachments([]);
          setContextDocuments([]);
          setAttachmentError(null);
        }

        sessionIdRef.current = currentSessionId;
        setSessionId(currentSessionId);
      }
    };
    initSession();
  }, [user?.id, currentSessionId]); // Re-run if user or currentSessionId changes

  // Sync loading state with context - use session-specific check for parallel search support
  // This ensures the loading indicator shows when THIS session is searching,
  // even if user switches away and back while search is in progress
  const effectiveIsLoading = isLoading || (sessionId ? isSessionSearching(sessionId) : false);
  // Die ausführliche Schicht einer Antwort belegt den Laufplatz des Chats: eine neue Frage erst nach ihrem Ende.
  const composerBusy = effectiveIsLoading || (sessionId ? isDetailRunning(sessionId) : false);
  const liveSearch = sessionId ? getLiveSearch(sessionId) : null;
  const currentResearch = sessionId ? getSessionResearch(sessionId) : null;
  const displayedMessages = mergeSessionMessages(messages, currentResearch || undefined);
  const latestUserQuestion = (): string => displayedMessages.filter(item => item.role === 'user').pop()?.content || '';
  /**
   * Der Endzustand unter einer Frage ohne Antwort: aus diesem Tab (der Lauf endete gerade) oder vom Server (Reload,
   * anderes Gerät). Läuft für die Frage ein neuer Lauf, ersetzt sein Fortschritt den Status.
   */
  const runEndOf = (message: RISMessage): ResearchRunEnd | undefined => (
    message.role === 'user' && message.messageId ? runEndForQuestion(message.messageId, endedRuns, currentResearch) : undefined
  );
  // Das Ende des Laufs dieses Tabs gilt auch, nachdem eine weitere Frage den Lauf des Chats übernommen hat.
  useEffect(() => {
    setEndedRuns(previous => nextEndedRuns(previous, currentResearch));
  }, [currentResearch]);
  // Endet der Lauf im geöffneten Chat, ist sein Ergebnis gesehen; die Seitenleiste setzt dafür keinen Punkt „neu“. Die
  // Recherche-Kennung der Antwort ist auf jeder Bahn die Lauf-ID.
  const finishedRunId = currentResearch
    ? currentResearch.status === 'completed' ? currentResearch.assistantMessage?.researchId
      : currentResearch.status === 'failed' ? currentResearch.runEnd?.runId : undefined
    : undefined;
  useEffect(() => {
    if (sessionId && finishedRunId) reportRunSeen(sessionId, finishedRunId);
  }, [sessionId, finishedRunId, reportRunSeen]);
  // Ebenso das Ende eines Laufs der ausführlichen Schicht im geöffneten Chat.
  const finishedDetailRunIds = endedDetailRunIds(detailRuns, sessionId).join(' ');
  useEffect(() => {
    if (sessionId) for (const runId of finishedDetailRunIds.split(' ').filter(Boolean)) reportRunSeen(sessionId, runId);
  }, [sessionId, finishedDetailRunIds, reportRunSeen]);
  const lastDisplayedMessage = displayedMessages[displayedMessages.length - 1];

  // Quellenadressen aus Kollektivvertrag und AGB werden auf Dokumente des
  // eigenen Korpus aufgelöst, damit sie im Dokument-Viewer mit Trefferstellen
  // geöffnet werden. Der Zwischenspeicher des Dienstes macht wieder geöffnete
  // Verläufe billig; ein Fehlschlag lässt die Darstellung unverändert.
  const [resolvedDocuments, setResolvedDocuments] = useState<ResolvedDocumentMap>(() => new Map());
  const documentSourceKey = resolvableSourceUrls([
    ...displayedMessages.flatMap(documentUrlCandidates),
    // Der laufende Text bleibt aussen vor: eine halb übertragene Adresse wäre
    // eine andere Adresse. Die Verweise im Text entstehen, sobald die Antwort
    // steht und die Nachricht in der Liste erscheint.
    ...(liveSearch ? documentUrlCandidates({
      content: '',
      sources: liveSearch.sources,
      verifiedSources: liveSearch.verifiedSources,
    }) : []),
  ]).join('\n');

  useEffect(() => {
    const urls = documentSourceKey ? documentSourceKey.split('\n') : [];
    if (urls.length === 0) {
      setResolvedDocuments(previous => (previous.size === 0 ? previous : new Map()));
      return;
    }
    let active = true;
    void resolveDocumentSources(urls).then(resolved => {
      if (active) setResolvedDocuments(resolved);
    });
    return () => { active = false; };
  }, [documentSourceKey]);

  // Die laufende Antwort trägt dieselben Felder wie die gespeicherte, damit sie
  // dieselben Schichten zeigt: Klartext, Chips und die ausführliche Schicht.
  const liveResearchMetadata: ResearchMetadata | undefined = currentResearch?.useLaneB ? {
    lane: resolvedStagingLane(liveSearch?.lane),
    ...(liveSearch?.lane === 'D' && liveSearch.modelPreset ? { modelPreset: liveSearch.modelPreset } : {}),
    ...(liveSearch?.answerProfile ? { answerProfile: liveSearch.answerProfile } : {}),
    ...(liveSearch?.detailedAnswer ? { detailedAnswer: liveSearch.detailedAnswer } : {}),
    ...(liveSearch?.suggestions ? { suggestions: liveSearch.suggestions } : {}),
  } : undefined;

  const footerMessages = sourcesFooterMessages(
    displayedMessages,
    liveSearch,
    currentResearch?.assistantMessage,
    liveResearchMetadata,
  );

  // Icon for RISearch 2.0
  const getModeIcon = (className: string = "w-8 h-8") => {
    return <MagnifyingGlassIcon className={className} />;
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [displayedMessages.length, liveSearch?.answer, liveSearch?.statusMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Session Event Handlers
  const handleSessionSelect = async (selectedSessionId: string) => {
    // Issue a new selection even while an older load is pending; its response
    // is rejected by LatestSelection below.
    if (selectedSessionId === sessionId) {
      console.log(`[RIS] Skipping session select - same session`);
      return;
    }

    console.log(`[RIS] Loading session: ${selectedSessionId}`);
    const selection = selectionRef.current.issue(selectedSessionId);
    sessionIdRef.current = selectedSessionId;
    activeSubmitIdRef.current = null;
    setIsLoading(false);
    setCurrentSessionId(selectedSessionId);
    setSessionId(selectedSessionId);
    // Noch nicht mitgeschickte Anhänge gehören zu ihrer Sitzung und stehen
    // beim Zurückkehren wieder in der Leiste.
    setAttachments([]);
    setContextDocuments([]);
    setAttachmentError(null);
    void listChatAttachments(selectedSessionId)
      .then(({ open, context }) => {
        if (!selectionRef.current.isCurrent(selection, sessionIdRef.current)) return;
        setAttachments(open);
        setContextDocuments(context);
      })
      .catch(() => undefined);

    try {
      setIsLoadingSession(true);
      setError(null);
      setMeldeFehlercode(null);
      setMessages([]); // Clear messages immediately to show loading state
      setEndedRuns({});

      // Always load persisted history. The session-keyed research overlay keeps
      // an in-flight question visible if persistence and this GET cross paths.
      const { session, messages: sessionMessages } = await webChatHistoryService.getChatSession(selectedSessionId);

      if (!session || !sessionMessages) {
        throw new Error('Session not found');
      }

      if (!selectionRef.current.isCurrent(selection, sessionIdRef.current)) return;

      // Convert messages to RIS format
      const risMessages: RISMessage[] = sessionMessages.map(risMessageFromStored);

      if (!selectionRef.current.isCurrent(selection, sessionIdRef.current)) return;
      setMessages(risMessages);
      setEndedRuns(Object.fromEntries((session.endedRuns || []).map(({ questionMessageId, ...runEnd }) => [questionMessageId, runEnd])));
      // Das Öffnen des Chats meldet seine Markierung „neu“ als gesehen, auf jedem Gerät.
      if (session.unseenRun) reportRunSeen(selectedSessionId, session.unseenRun.id);
      // Ein Lauf, der noch läuft, wird ab seinem letzten Ereignis wieder verfolgt, mit dem Auswerter seiner Bahn; ein
      // beendeter steht schon im Verlauf.
      const activeRun = session.activeRun;
      const runQuestion = [...risMessages].reverse().find(message => message.role === 'user');
      if (activeRun?.kind === 'research' && runQuestion) {
        void riSearchContext.resumeSessionRun(selectedSessionId, activeRun.id, runQuestion, activeRun)
          .then(result => { if (result && result !== 'locked') triggerSidebarRefresh(); });
      }
      // Die ausführliche Schicht an ihrer Antwort (questionMessageId nennt die Ziel-Nachricht).
      if (activeRun?.kind === 'detail' && activeRun.questionMessageId) {
        void resumeDetailRun(selectedSessionId, activeRun.id, activeRun.questionMessageId).then(() => triggerSidebarRefresh());
      }
      const researchAtLoad = getSessionResearch(selectedSessionId);
      if (researchAtLoad?.status === 'completed') {
        const persistedIds = new Set(risMessages.map(message => message.messageId).filter(Boolean));
        if (researchAtLoad.userMessage.messageId && researchAtLoad.assistantMessage?.messageId
          && persistedIds.has(researchAtLoad.userMessage.messageId)
          && persistedIds.has(researchAtLoad.assistantMessage.messageId)) {
          acknowledgeSessionResearch(selectedSessionId);
        }
      }

      console.log(`[RIS] Loaded session: ${selectedSessionId} with ${risMessages.length} messages`);

      // Restore session history in backend for continued conversation context
      if (risMessages.length > 0) {
        try {
          // Cookies are sent automatically with withCredentials: true
          console.log(`[Frontend] Restoring backend session context for: ${selectedSessionId}`);
          const response = await axios.post(buildApiUrl('/api/ris/restore-session-history'), {
            sessionId: selectedSessionId,
            history: risMessages
          }, {
            withCredentials: true
          });

          if (response.data.success) {
            console.log(`[Frontend] Backend session context restored with ${risMessages.length} messages`);
          } else {
            console.warn('[RISAssistant] Backend session restore was rejected');
          }
        } catch {
          console.warn('[RISAssistant] Backend session restore failed');
        }
      }

    } catch {
      if (!selectionRef.current.isCurrent(selection, sessionIdRef.current)) return;
      console.error('[RISAssistant] Session load failed');
      setError('Die Recherche konnte nicht geladen werden.');
      setMeldeFehlercode(null);
    } finally {
      if (selectionRef.current.isCurrent(selection, sessionIdRef.current)) setIsLoadingSession(false);
    }
  };

  /**
   * Eine ausgewählte Datei geht sofort zum Server; erst von dort kommt der
   * Chip zurück. Bis dahin bleibt der Senden-Knopf gesperrt, damit keine
   * Frage ohne ihre Unterlage losgeht.
   */
  const handleAttachmentSelection = async (selected: FileList | readonly File[] | null): Promise<ChatAttachmentSummary[]> => {
    const files = selected ? Array.from(selected) : [];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (files.length === 0) return [];
    setAttachmentError(null);

    const selectionError = attachmentSelectionError(attachments.length, files.length);
    if (selectionError) {
      setAttachmentError(selectionError);
      return [];
    }
    // Zu große Dateien bleiben hier; die übrigen gehen trotzdem hoch.
    const { accepted: uploadable, error: sizeError } = splitOversizedAttachments(files);
    if (sizeError) setAttachmentError(sizeError);
    if (uploadable.length === 0) return [];
    let activeSessionId = sessionId;
    if (!activeSessionId) {
      activeSessionId = await webChatHistoryService.generateSessionId('ris', user?.id);
      setSessionId(activeSessionId);
      setCurrentSessionId(activeSessionId);
    }
    // Die Sitzung muss am Server stehen, bevor sie einen Anhang tragen kann.
    try {
      await webChatHistoryService.createChatSessionV2({
        sessionId: activeSessionId,
        title: 'Neue Recherche',
        serviceType: 'ris',
      });
    } catch {
      // Gibt es die Sitzung schon, ist alles in Ordnung.
    }

    setUploadingCount(uploadable.length);
    try {
      // Jede fertige Gruppe steht sofort als Chips da; der Zähler der
      // wartenden Dateien sinkt mit.
      const uploaded = await uploadChatAttachments(activeSessionId, uploadable, undefined, (batch, batchFiles) => {
        setAttachments(previous => [...previous, ...batch]);
        setUploadingCount(previous => Math.max(0, previous - batchFiles));
      });
      const failed = uploaded.find(attachment => attachment.status === 'failed');
      if (failed) {
        setAttachmentError(failed.errorMessage || `Die Datei "${failed.fileName}" konnte nicht verarbeitet werden.`);
      }
      return uploaded;
    } catch (uploadError) {
      setAttachmentError(attachmentRequestError(uploadError));
      return [];
    } finally {
      setUploadingCount(0);
    }
  };

  /** Die offenen Anhänge und die Dokumente im Kontext neu vom Server, solange der Chat noch angezeigt wird. */
  const reloadAttachments = (targetSessionId: string) => {
    void listChatAttachments(targetSessionId)
      .then(({ open, context }) => {
        if (sessionIdRef.current !== targetSessionId) return;
        setAttachments(open);
        setContextDocuments(context);
      })
      .catch(() => undefined);
  };

  // AG-6: das x nimmt ein Dokument dauerhaft aus dem Kontext, das + holt es zurück.
  const handleContextToggle = async (attachmentId: string, inContext: boolean) => {
    if (!sessionId) return;
    const previous = contextDocuments;
    setContextDocuments(previous.map(document => (document.id === attachmentId ? { ...document, inContext } : document)));
    setAttachmentError(null);
    try {
      await setChatAttachmentInContext(sessionId, attachmentId, inContext);
    } catch (toggleError) {
      setContextDocuments(previous);
      setAttachmentError(attachmentRequestError(toggleError));
    }
  };

  // Eine Datei, die der Server nicht mehr hat: die neue Datei geht als Anhang der nächsten Frage, die alte aus dem Kontext.
  const handleContextReupload = (attachmentId: string) => {
    reuploadTargetRef.current = attachmentId;
    reuploadInputRef.current?.click();
  };
  const handleReuploadSelection = async (selected: FileList | null) => {
    const target = reuploadTargetRef.current;
    const files = selected ? Array.from(selected) : [];
    reuploadTargetRef.current = null;
    if (reuploadInputRef.current) reuploadInputRef.current.value = '';
    if (!target) return;
    const uploaded = await handleAttachmentSelection(files);
    if (uploaded.some(attachment => attachment.status !== 'failed')) await handleContextToggle(target, false);
  };

  const handleAttachmentRemove = async (attachmentId: string) => {
    const previous = attachments;
    setAttachments(previous.filter(attachment => attachment.id !== attachmentId));
    setAttachmentError(null);
    if (!sessionId) return;
    try {
      await deleteChatAttachment(sessionId, attachmentId);
    } catch (removeError) {
      setAttachments(previous);
      setAttachmentError(attachmentRequestError(removeError));
    }
  };

  // Ohne useCallback: die Frage nimmt stets die aktuelle Bahn, das Modell und die Anhänge mit.
  const submitResearch = async (question: string) => {
    const query = question.trim();
    // Check both local and context loading states
    if (!query || composerBusy || uploadingCount > 0 || activeSubmitIdRef.current) return;

    if (query.length < 10) {
      setError('Bitte geben Sie eine aussagekräftige Frage mit mindestens 10 Zeichen ein.');
      setMeldeFehlercode(null);
      return;
    }

    // Jede Frage läuft auf der Bahn B; das Modell bleibt für diesen Lauf festgehalten.
    const pinnedLaneSelection = pinnedChatModelSelection(
      laneSelection,
      isDeveloper,
      laneDPresets,
    );

    // Anhänge gehören zur Frage. Nimmt sie eine Bahn, die keinen Fallkontext
    // kennt, bleibt die Frage stehen -- lieber ein Hinweis als eine Antwort,
    // die ihre Unterlagen nie gesehen hat.
    if ((attachments.length > 0 || contextDocuments.some(document => document.inContext && document.available))
      && !showsChatUpload(isDeveloper, pinnedLaneSelection.variant, laneDPresets)) {
      setAttachmentError(
        'Anhänge erreichen die Recherche nicht auf dem lokalen Modell der Bahn C. '
        + 'Bitte wählen Sie ein anderes Modell, entfernen Sie neue Anhänge oder nehmen Sie Dokumente '
        + 'über das x am Chip aus dem Kontext.',
      );
      return;
    }

    // Track this submit so old handleSubmit's finally can't kill our loading state
    const submitId = `${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    activeSubmitIdRef.current = submitId;

    // Die Anhänge gehören ab jetzt zu dieser Frage; der Verfasser beginnt mit
    // leerer Leiste.
    const pinnedAttachments = attachments;
    setAttachments([]);
    setAttachmentError(null);
    setInputValue('');
    setIsLoading(true);
    setError(null);
    setMeldeFehlercode(null);
    let submittedSessionId: string | null = null;
    let researchLocked = false;

    try {
      let localSessionId = sessionId;

      // Ensure we have a session ID
      if (!localSessionId) {
        console.warn(`[RIS] No session ID found, generating fallback...`);
        localSessionId = await webChatHistoryService.generateSessionId('ris', user?.id);
        setSessionId(localSessionId);
        setCurrentSessionId(localSessionId);
        console.warn(`[RIS] Generated fallback session ID: ${localSessionId}`);
      } else {
        console.log(`[RIS] Using existing session ID: ${localSessionId}`);
      }

      // Capture session ID before await to detect stale closure
      const capturedSessionId = localSessionId;
      submittedSessionId = localSessionId;
      // Die Wahl gehört ab jetzt zu diesem Chat: seine Folgefragen laufen mit
      // demselben Modell, auch wenn die Sitzung eben erst entstanden ist.
      rememberLaneSelection(localSessionId, pinnedLaneSelection);

      // Delegate to global context - this will continue even if user navigates away
      // startSearch now returns the result directly to avoid React state timing issues
      const result = await riSearchContext.startSearch(
        query,
        selectedMode,
        localSessionId,
        displayedMessages,
        true,
        pinnedLaneSelection,
        false,
        pinnedAttachments,
      );

      if (result === 'locked') {
        researchLocked = true;
        return;
      }
      // The context keeps the result keyed by session, so reverse-order
      // completions cannot overwrite another chat's answer.
      if (result) {
        console.log(`[RIS] Search completed for session ${capturedSessionId}`);
        triggerSidebarRefresh();
      }

    } catch (error: any) {
      // F13-6: bei einer Recherchesperre übernimmt die Sperrseite; kein Konsolenfehler, kein roter Hinweis.
      if (istRechercheSperre(error)) {
        researchLocked = true;
        return;
      }
      console.error('[RISAssistant] Research submission failed');
      // Only set error if this submit is still the active one
      if (activeSubmitIdRef.current === submitId) {
        setError(error?.userMessage || riSearchContext.searchError || ErrorHandler.researchErrorMessage(error));
        // Profil-Nachbau F-06 (BAU-HILFE.md A-10, PF-316; TEIL-3 SP3-01 „Weitere Einstiege“): Code für den Link zum Formular.
        setMeldeFehlercode(meldeCode(error));
      }
    } finally {
      // Only clear isLoading if this submit is still the active one
      // Prevents old search's finally from killing a new search's loading state
      if (activeSubmitIdRef.current === submitId) {
        activeSubmitIdRef.current = null;
        setIsLoading(false);
      }
      // AG-6: die Anhänge der Frage stehen danach im Kontext; eine abgewiesene Frage behält sie als offene Anhänge.
      // F13-6: nach einer Sperre auch im finally keine weitere Anfrage.
      if (submittedSessionId && !researchLocked) reloadAttachments(submittedSessionId);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void submitResearch(inputValue);
  };

  // „Wiederholen“: ein neuer Lauf zur bestehenden Frage; die Frage bleibt einmal im Verlauf, darunter die neue Antwort.
  const retryRun = async (question: RISMessage, runEnd: ResearchRunEnd) => {
    if (!sessionId || composerBusy) return;
    setError(null);
    setMeldeFehlercode(null);
    if (question.messageId) {
      const questionId = question.messageId;
      setEndedRuns(previous => forgetEndedRun(previous, questionId));
    }
    try {
      const result = await riSearchContext.retryRun(sessionId, runEnd, question);
      if (result && result !== 'locked') triggerSidebarRefresh();
    } catch (retryError) {
      setError(ErrorHandler.handleApiError(retryError));
      setMeldeFehlercode(null);
    }
  };

  const retryFailedSearch = async () => {
    if (!sessionId || currentResearch?.status !== 'failed') return;
    setError(null);
    setMeldeFehlercode(null);
    try {
      const result = await riSearchContext.startSearch(
        currentResearch.userMessage.content,
        currentResearch.userMessage.mode || selectedMode,
        sessionId,
        messages,
        // Auch der zweite Versuch einer Frage der ruhenden Bahn A läuft auf der Bahn B.
        true,
        // Ein zweiter Versuch nimmt dasselbe Modell wie der gescheiterte Lauf.
        retriedLaneSelection(currentResearch.laneVariant, currentResearch.modelPreset),
        true,
      );
      if (result !== 'locked') triggerSidebarRefresh();
    } catch (retryError) {
      setError(ErrorHandler.handleApiError(retryError));
      setMeldeFehlercode(null);
    }
  };

  // Consume the explicit home-page submission only after its new chat is ready.
  // Clear history before starting so refresh/back cannot send the question again.
  useEffect(() => {
    const routeState = location.state as {
      researchPrompt?: unknown;
      submitResearch?: unknown;
      researchSessionId?: unknown;
    } | null;
    if (typeof routeState?.researchPrompt !== 'string' || !routeState.researchPrompt.trim()) return;
    if (consumedStartRef.current === location.key) return;
    if (!sessionId || sessionId !== currentSessionId || pendingSessionLoad) return;

    const query = routeState.researchPrompt.trim();
    const shouldSubmit = routeState.submitResearch === true && routeState.researchSessionId === sessionId;
    if (shouldSubmit && composerBusy) return;

    consumedStartRef.current = location.key;
    navigate(location.pathname, { replace: true, state: null });
    if (shouldSubmit && query.length >= 10) {
      void submitResearch(query);
    } else {
      setInputValue(query);
      textareaRef.current?.focus();
    }
  }, [location.key, location.pathname, location.state, navigate, sessionId, currentSessionId, pendingSessionLoad, composerBusy, submitResearch]);

  const copyToClipboard = async (text: string, type?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(`${text}-${type || 'default'}`);
      setTimeout(() => setCopiedId(''), 2000);
    } catch {
      console.error('[RISAssistant] Clipboard write failed');
    }
  };

  const copyMessageWithSources = async (message: RISMessage) => {
    try {
      await navigator.clipboard.writeText(copyTextOfMessage(message));
      setCopiedId(`${message.content}-with-sources`);
      setTimeout(() => setCopiedId(''), 2000);
    } catch {
      console.error('[RISAssistant] Message clipboard write failed');
    }
  };
  // Schlusslauf (pruefung-slb M2, Regeln 09 und 12): mindestens 44 × 44 px und 14 px Schrift. Mit Antwortbewertung steht der
  // Knopf in deren Knopfzeile (aktion), damit ihr Formular die ganze Breite der Leiste hat.
  const kopierKnopf = (message: RISMessage) => (
    <button
      onClick={() => copyMessageWithSources(message)}
      className="ml-auto shrink-0 flex items-center justify-center gap-1 min-h-11 min-w-11 px-3 py-2 text-sm text-muted hover:text-primary dark:text-gray-400 dark:hover:text-white border border-warm-200 dark:border-gray-600 rounded-lg hover:bg-warm-100 dark:hover:bg-gray-700 transition-colors"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
      <span>{copiedId === `${message.content}-with-sources` ? 'Kopiert' : 'Kopieren'}</span>
    </button>
  );

  // F-09: der Knopf schreibt dieselbe Kontoeinstellung wie die Seite „Präferenzen“; bis zur Antwort gilt der neue Wert, bei
  // Fehler springt er zurück. In einer Sicht gilt er nur in dieser Ansicht (der Server antwortet dort 403).
  const toggleChatMode = () => {
    const neu = !chatMode;
    setChatMode(neu);
    if (user?.sicht?.aktiv) return;
    void apiClient.put('/api/auth/preferences', { sendWithEnter: neu }).then(() => checkAuth(), () => setChatMode(!neu));
  };

  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 180) + 'px';
    }
  };

  // Ein Weiterfragen-Chip trägt seine Frage in das Eingabefeld; abgeschickt
  // wird sie vom Nutzer, nicht vom Chip.
  const fillComposerWithSuggestion = (question: string) => {
    setInputValue(question);
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      autoResizeTextarea();
    });
  };

  // „Mit Enter senden“ an: Enter sendet, Shift+Enter macht eine neue Zeile; aus (Vorgabe): umgekehrt. Während einer Eingabe
  // mit Eingabemethode sendet Enter nie (praeferenzen.ts enterSendet).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (enterSendet({ key: e.key, shiftKey: e.shiftKey, isComposing: e.nativeEvent.isComposing }, chatMode)) {
      e.preventDefault();
      if (inputValue.trim() && !composerBusy && uploadingCount === 0) handleSubmit(e as unknown as React.FormEvent);
    }
  };

  const clearSession = () => {
    setMessages([]);
    setEndedRuns({});
    setSessionId(null);
    setError(null);
    setMeldeFehlercode(null);
  };


  // ===== Collapsible Sections Helpers (E and F) =====
  const [expandedSections, setExpandedSections] = useState<Record<string, { E: boolean; F: boolean }>>({});

  const toggleSection = (messageKey: string, section: 'E' | 'F') => {
    setExpandedSections(prev => {
      const current = prev[messageKey] || { E: false, F: false };
      return { ...prev, [messageKey]: { ...current, [section]: !current[section] } };
    });
  };


  /** Eine beanstandete Fundstelle öffnet wie jede andere Quelle der Antwort. */
  const openObjectionSource = (message: RISMessage) => (target: ViewerSourceTarget) => {
    void openViewerSource(
      target,
      { query: latestUserQuestion(), answer: message.content || '' },
      { openViewer, openDocumentViewer },
    );
  };

  /**
   * Holt die ausführliche Schicht einer gespeicherten Antwort als Lauf am Server; auch der erneute Versuch nach einem
   * gescheiterten. Ein laufender wird nicht verdoppelt; eine schon gespeicherte Schicht kommt ohne zweiten Lauf.
   */
  const startDetailFetch = (messageId: string) => {
    const chatSessionId = sessionIdRef.current;
    if (!messageId || !chatSessionId) return;
    void startDetailRun(chatSessionId, messageId).then(() => triggerSidebarRefresh());
  };

  const renderAssistantMessageContent = (
    message: RISMessage,
    index: number,
    options: { streaming?: boolean } = {},
  ) => {
    const messageKey = message.messageId || `msg-${index}`;
    const content = message.content || '';
    const openRis = (url: string) => { void openViewer(url, latestUserQuestion(), content); };
    const openDocument = (documentId: string) => {
      void openDocumentViewer(documentId, latestUserQuestion(), content);
    };

    // Lane B's A-to-G answer is review evidence. Keep it fully visible instead
    // of applying the E/F preview collapse the standalone V25 answer keeps.
    // risLinkMetadata stammt allein aus dem ToolUse-Stack und bleibt deshalb
    // dem eigenen Rechercheweg vorbehalten.
    if (isStagingLaneMessage(message)) {
      const answerBody = (
        <>
          <StagingLaneLabel message={message} developer={isDeveloper} />
          <ResearchAnswerBody
            content={content}
            resolvedDocuments={resolvedDocuments}
            onOpenRisSource={openRis}
            onOpenDocumentSource={openDocument}
          />
        </>
      );
      if (!isKlartextAnswer(message)) return answerBody;
      // Die ausführliche Schicht kann gespeichert vorliegen oder gerade erst
      // abgerufen werden; beide Wege münden in denselben Text. Die Einwände
      // teilen sich danach auf, deshalb sieht die Aufteilung den abgerufenen
      // Text ebenso wie den gespeicherten.
      const detail = message.messageId ? detailRuns[message.messageId]?.state : undefined;
      const detailedAnswer = message.researchMetadata.detailedAnswer || detail?.text || undefined;
      const layeredMessage = detailedAnswer === message.researchMetadata.detailedAnswer
        ? message
        : { ...message, researchMetadata: { ...message.researchMetadata, detailedAnswer } };
      return (
        <>
          {answerBody}
          {detail?.restartNotice && <ResearchRestartNotice notice={detail.restartNotice} />}
          <AnswerLayers
            messageKey={messageKey}
            detailedAnswer={detailedAnswer}
            detailAvailable={message.researchMetadata.detailAvailable === true && Boolean(message.messageId)}
            detailStatus={detail?.status}
            detailStatusMessage={detail?.statusMessage}
            detailErrorMessage={detail?.errorMessage}
            onExpandDetail={message.messageId ? () => startDetailFetch(message.messageId!) : undefined}
            onRetryDetail={message.messageId ? () => startDetailFetch(message.messageId!) : undefined}
            suggestions={message.researchMetadata.suggestions || detail?.suggestions}
            reviewAnnotation={message.reviewAnnotation
              || message.researchMetadata.reviewAnnotation
              || detail?.reviewAnnotation}
            streaming={options.streaming === true}
            objections={(
              <MessageReviewObjections
                message={layeredMessage}
                layer="detailed"
                resolvedDocuments={resolvedDocuments}
                onOpenSource={openObjectionSource(message)}
              />
            )}
            renderDetailedSection={sectionText => (
              <ResearchAnswerBody
                content={sectionText}
                resolvedDocuments={resolvedDocuments}
                onOpenRisSource={openRis}
                onOpenDocumentSource={openDocument}
              />
            )}
            onAskSuggestion={fillComposerWithSuggestion}
          />
        </>
      );
    }

    return (
      <CollapsibleAnswerBody
        content={content}
        resolvedDocuments={resolvedDocuments}
        risLinkMetadata={message.risLinkMetadata}
        expanded={expandedSections[messageKey] || { E: false, F: false }}
        onToggleSection={section => toggleSection(messageKey, section)}
        onOpenRisSource={openRis}
        onOpenDocumentSource={openDocument}
      />
    );
  };

  // Die Klartext-Antwort führt ihre Quellen im Fußbereich des Gesprächs.
  const renderResearchSources = (message: RISMessage) => (
    isKlartextAnswer(message) ? null : (
    <ResearchSourceList
      sources={message.sources || []}
      answer={message.content}
      resolvedDocuments={resolvedDocuments}
      // Wie im Antwortkörper: die gemeldeten Fundstellen gehören dem eigenen
      // Rechercheweg, Bahn B stellt ihre Antwort ohne sie dar.
      risLinkMetadata={isStagingLaneMessage(message) ? undefined : message.risLinkMetadata}
      onOpenSource={target => {
        void openViewerSource(
          target,
          { query: latestUserQuestion(), answer: message.content },
          { openViewer, openDocumentViewer },
        );
      }}
    />
    )
  );

  const renderResearchReview = (message: RISMessage) => {
    const review = message.reviewAnnotation || message.researchMetadata?.reviewAnnotation;
    // Bei der Klartext-Antwort trägt der Kopf der ausführlichen Schicht das
    // Prüfabzeichen und der Fußbereich die geprüften Quellen; hier bleiben nur
    // die Einwände stehen, die der Leser sehen muss.
    const klartext = isKlartextAnswer(message);
    const verifiedSources = klartext ? [] : (message.verifiedSources || []);
    if (!messageShowsReviewBlock(message)) return null;

    const objectionCount = review?.objections.length || 0;
    const badgeLabel = review?.status === 'certified'
      ? 'Geprüft'
      : review?.status === 'objections'
        ? `Geprüft, ${objectionCountLabel(objectionCount)}`
        : 'Prüfung nicht verfügbar';
    const badgeClass = review?.status === 'certified'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
      : review?.status === 'objections'
        ? 'border-amber-300 bg-amber-50 text-amber-900'
        : 'border-warm-200 bg-warm-100 text-muted';

    return (
      <div className="mt-4 space-y-3 border-t border-warm-200 pt-3 dark:border-gray-700">
        {review && (
          <div>
            {!klartext && (
              <span className={`inline-flex border px-2.5 py-1 text-xs font-semibold ${badgeClass}`}>
                {badgeLabel}
              </span>
            )}
            {review.status === 'objections' && (
              <MessageReviewObjections
                message={message}
                layer="answer"
                resolvedDocuments={resolvedDocuments}
                onOpenSource={openObjectionSource(message)}
              />
            )}
          </div>
        )}

        {verifiedSources.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted dark:text-gray-400">
              Geprüfte Quellen
            </p>
            <ul className="mt-2 space-y-1.5 text-sm">
              {verifiedSources.map((source, sourceIndex) => {
                // Eine geprüfte Quelle ohne Dokumentkennung kann über ihre
                // Adresse trotzdem im eigenen Korpus liegen.
                const resolvableUrl = source.documentId ? null : resolvableSourceUrl(source.risLink);
                const resolvedDocument = resolvableUrl ? resolvedDocuments.get(resolvableUrl) : undefined;
                return (
                <li key={`${source.reference}-${sourceIndex}`} className="flex items-start gap-2">
                  <span className={source.found ? 'text-emerald-700' : 'text-amber-700'} aria-hidden="true">
                    {source.found ? '✓' : '–'}
                  </span>
                  {source.documentId ? (
                    <button
                      type="button"
                      onClick={() => openDocumentViewer(source.documentId!, displayedMessages.filter(item => item.role === 'user').pop()?.content || '', message.content)}
                      className="text-left font-medium text-primary underline hover:text-accent-dark dark:text-gray-200"
                    >
                      {source.title || source.reference}
                    </button>
                  ) : resolvedDocument ? (
                    <span className="flex flex-wrap items-baseline gap-2">
                      <button
                        type="button"
                        onClick={() => openDocumentViewer(resolvedDocument.documentId, latestUserQuestion(), message.content)}
                        className="text-left font-medium text-primary underline hover:text-accent-dark dark:text-gray-200"
                      >
                        {resolvedDocument.title || source.title || source.reference}
                      </button>
                      <a
                        href={resolvedDocument.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-muted underline hover:text-primary dark:text-gray-400"
                      >
                        Originalquelle
                      </a>
                    </span>
                  ) : source.risLink ? (
                    <button
                      type="button"
                      onClick={() => openViewer(source.risLink!, displayedMessages.filter(item => item.role === 'user').pop()?.content || '', message.content)}
                      className="text-left font-medium text-primary underline hover:text-accent-dark dark:text-gray-200"
                    >
                      {source.title || source.reference}
                    </button>
                  ) : (
                    <span className="text-primary dark:text-gray-200">{source.title || source.reference}</span>
                  )}
                </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderDeepRISearchData = (deepData: DeepRISearchResponse) => {
    return (
      <div className="mt-4 space-y-4">
        {/* Processing Information */}
        <div className="bg-warm-100 border border-warm-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <MagnifyingGlassIcon className="w-5 h-5 text-accent" />
            <h4 className="font-semibold text-primary">Vertiefte Analyse</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600 mb-1">Verarbeitungszeit</p>
              <p className="font-medium text-accent">{Math.round(deepData.metadata.processingTime / 1000)}s</p>
            </div>
            {deepData.metadata.jurisprudenceStats && (
              <>
                <div>
                  <p className="text-gray-600 mb-1">RIS Ergebnisse</p>
                  <p className="font-medium text-accent">{deepData.metadata.jurisprudenceStats.searchResults}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Urteile durchgelesen</p>
                  <p className="font-medium text-accent">
                    {deepData.metadata.jurisprudenceStats.extractedCases + (deepData.metadata.jurisprudenceStats.extractedUrteile || 0)}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStructuredData = (structuredData: StructuredCaseLawResponse) => {
    const getStrengthColor = (strength: string) => {
      switch (strength) {
        case 'VERY_STRONG': return 'text-green-600 bg-green-100';
        case 'STRONG': return 'text-green-500 bg-green-50';
        case 'MODERATE': return 'text-yellow-600 bg-yellow-100';
        case 'WEAK': return 'text-red-600 bg-red-100';
        default: return 'text-gray-600 bg-gray-100';
      }
    };

    const getConsistencyColor = (consistency: string) => {
      switch (consistency) {
        case 'HIGH': return 'text-green-600';
        case 'MEDIUM': return 'text-yellow-600';
        case 'LOW': return 'text-red-600';
        default: return 'text-gray-600';
      }
    };

    return (
      <div className="mt-4 space-y-4">
        {/* Executive Summary */}
        <div className="bg-warm-100 border border-warm-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-accent" />
            <h4 className="font-semibold text-primary">Strategische Bewertung</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Erfolgswahrscheinlichkeit</p>
              <p className="text-xl font-bold text-accent">{structuredData.executiveSummary.successProbability}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Gesamtbewertung</p>
              <p className="text-sm">{structuredData.executiveSummary.strategicAssessment}</p>
            </div>
          </div>

          {structuredData.executiveSummary.keyStrengths.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-green-700 mb-1">Stärken:</p>
              <ul className="text-sm text-green-600 list-disc list-inside">
                {structuredData.executiveSummary.keyStrengths.map((strength, index) => (
                  <li key={index}>{strength}</li>
                ))}
              </ul>
            </div>
          )}

          {structuredData.executiveSummary.potentialWeaknesses.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-red-700 mb-1">Schwächen:</p>
              <ul className="text-sm text-red-600 list-disc list-inside">
                {structuredData.executiveSummary.potentialWeaknesses.map((weakness, index) => (
                  <li key={index}>{weakness}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Quality Metrics */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calculator className="w-5 h-5 text-gray-600" />
            <h4 className="font-semibold text-gray-900">Qualitätsmetriken</h4>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Entscheidungen</p>
              <p className="text-lg font-bold text-gray-900">{structuredData.qualityMetrics.totalRelevantDecisions}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Confidence</p>
              <p className="text-lg font-bold text-gray-900">{structuredData.qualityMetrics.averageConfidenceScore}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Konsistenz</p>
              <p className={`text-sm font-medium ${getConsistencyColor(structuredData.qualityMetrics.jurisprudenceConsistency)}`}>
                {structuredData.qualityMetrics.jurisprudenceConsistency}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Stärke</p>
              <span className={`text-xs px-2 py-1 rounded-full ${getStrengthColor(structuredData.qualityMetrics.argumentationStrength)}`}>
                {structuredData.qualityMetrics.argumentationStrength}
              </span>
            </div>
          </div>
        </div>

        {/* Primary Jurisprudence */}
        {structuredData.primaryJurisprudence.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-green-900">Hauptstützende Rechtsprechung</h4>
            </div>

            <div className="space-y-3">
              {structuredData.primaryJurisprudence.slice(0, 3).map((decision, index) => (
                <div key={index} className="bg-white border border-green-200 rounded p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-sm font-medium text-green-700">
                        {decision.rsNumber || decision.court}
                      </span>
                      {decision.date && (
                        <span className="text-sm text-gray-500 ml-2">{decision.date}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                        {decision.significance}
                      </span>
                      <span className="text-xs px-2 py-1 bg-accent/10 text-accent-dark rounded">
                        {decision.confidenceScore}%
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-700 mb-2">{decision.coreRuling}</p>

                  {decision.directApplicability && (
                    <p className="text-xs text-gray-600 mb-2">
                      <strong>Anwendbarkeit:</strong> {decision.directApplicability}
                    </p>
                  )}

                  {decision.risLink && (
                    <a
                      href={decision.risLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:text-accent-dark underline"
                    >
                      RIS-Link
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ready-to-Use Text Blocks */}
        {structuredData.readyToUseTextBlocks.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-yellow-600" />
              <h4 className="font-semibold text-yellow-900">Berufungsfertige Textbausteine</h4>
            </div>

            <div className="space-y-3">
              {structuredData.readyToUseTextBlocks.map((block, index) => (
                <div key={index} className="bg-white border border-yellow-200 rounded p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-yellow-700">{block.type}</span>
                    <button
                      onClick={() => copyToClipboard(block.text, `textblock-${index}`)}
                      className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                    >
                      {copiedId === `${block.text}-textblock-${index}` ? 'Kopiert' : 'Kopieren'}
                    </button>
                  </div>

                  <p className="text-sm text-gray-700 leading-relaxed">{block.text}</p>

                  {block.supportingRsNumbers.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-600">
                        <strong>Stützende RS-Nummern:</strong> {block.supportingRsNumbers.join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // DR-13 UI-06: mit dem Knopf für Anhänge (Entwicklerkonto, Bahn B) sind „Senden“ und „Abbrechen“ auf schmalen Schirmen
  // Zeichen mit Namen, damit der kurze Platzhalter in einer Zeile ins Eingabefeld passt.
  const mitAnhang = showsChatUpload(isDeveloper, laneSelection.variant, laneDPresets);
  const kompakt = schmal && mitAnhang;

  // Chat interface
  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="bg-warm-card dark:bg-gray-800 border-b border-warm-200 dark:border-gray-700 px-4 py-3 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 items-center space-x-3">
            <div className="flex min-w-0 items-center space-x-2">
              <span className="hidden text-2xl text-primary dark:text-white sm:inline">{getModeIcon()}</span>
              <div className="min-w-0">
                <h1 className="font-serif text-lg leading-tight text-primary dark:text-white tracking-tight sm:text-xl">
                  Österreichisches Recht recherchieren
                </h1>
                <div className="hidden flex-wrap items-center gap-2 md:flex">
                  <p className="text-xs text-muted dark:text-gray-400">Gesetze, Judikatur und Kollektivverträge mit nachvollziehbaren Quellen</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 sm:gap-3">
            {showsLaneVariantSwitch(isDeveloper, laneDPresets) && (
              <StagingLaneModelSelect
                selection={laneSelection}
                laneDPresets={laneDPresets}
                disabled={effectiveIsLoading}
                onSelect={(selection) => rememberLaneSelection(laneSelectionKey, selection)}
              />
            )}
            {showsComparisonEntry(isDeveloper, comparisonAvailable) && (
              <button
                type="button"
                onClick={() => navigate('/ris/compare')}
                title="A/B-Vergleich"
                className="flex items-center gap-2 whitespace-nowrap rounded-[3px] border border-warm-200 bg-warm-100 px-3 py-2 text-xs font-semibold text-primary transition-colors hover:border-accent hover:bg-warm-200"
              >
                <FlaskConical className="h-4 w-4 text-accent" aria-hidden="true" />
                {/* UI-06: schmal nur das Zeichen, damit Schalter und Vergleich in eine Zeile passen. */}
                <span className="sr-only sm:not-sr-only">A/B-Vergleich</span>
              </button>
            )}
          </div>
        </div>
      </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-warm-bg dark:bg-gray-900">
          {isLoadingSession && displayedMessages.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-4"></div>
              <div className="text-muted dark:text-gray-500 text-lg">Recherche wird geladen …</div>
            </div>
          ) : displayedMessages.length === 0 ? (
            <div className="mx-auto max-w-2xl py-14 text-center">
              <p className="editorial-eyebrow mb-3">Ihr Rechercheplatz</p>
              <h2 className="font-serif text-3xl text-primary dark:text-white">Womit möchten Sie beginnen?</h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-muted dark:text-gray-400">
                Stellen Sie eine konkrete Rechtsfrage. {brand.displayName} ordnet die Rechtslage ein und weist die verwendeten österreichischen Quellen aus.
              </p>
              {websucheAn && <WebsucheHinweis className="mx-auto mt-2 max-w-xl text-xs leading-5 text-muted dark:text-gray-400 [&_a]:underline [&_a]:text-primary dark:[&_a]:text-white" />}
              <div className="mt-6 grid gap-2 text-left text-xs text-muted sm:grid-cols-3">
                <span className="border border-warm-200 bg-warm-card px-3 py-2">Gesetzeslage klären</span>
                <span className="border border-warm-200 bg-warm-card px-3 py-2">Judikatur einordnen</span>
                <span className="border border-warm-200 bg-warm-card px-3 py-2">Kollektivvertrag prüfen</span>
              </div>
            </div>
          ) : null}

          {displayedMessages.map((message, index) => {
            const runEnd = runEndOf(message);
            return (
            <React.Fragment key={message.messageId || index}>
            <div
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`min-w-0 max-w-3xl rounded-2xl p-4 shadow-soft ${
                  message.role === 'user'
                    ? 'bg-primary text-white'
                    : 'bg-warm-card dark:bg-gray-800 border border-warm-200 dark:border-gray-700 text-primary dark:text-gray-100'
                }`}
              >
                {message.role === 'assistant' ? (
                  renderAssistantMessageContent(message, index)
                ) : (
                  <>
                    <div
                      className={`prose max-w-none break-words ${message.role === 'user' ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}
                      style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      dangerouslySetInnerHTML={nachrichtHtml(message.content, resolvedDocuments, message.risLinkMetadata)}
                    />
                    <ChatAttachmentNames attachments={message.attachments || []} />
                  </>
                )}

                {message.role === 'assistant' && renderResearchSources(message)}
                {message.role === 'assistant' && renderResearchReview(message)}

                {message.role === 'assistant' && (
                  <div className="mt-4 flex items-start justify-between gap-2 border-t border-warm-200 dark:border-gray-700 pt-3">
                    {message.researchId
                      ? <AnswerRating researchId={message.researchId} key={message.researchId} initial={message.rating ?? null} aktion={kopierKnopf(message)} />
                      : kopierKnopf(message)}
                  </div>
                )}

                {message.structuredData && (
                  renderStructuredData(message.structuredData)
                )}

                {message.deepRISearchData && (
                  renderDeepRISearchData(message.deepRISearchData)
                )}
              </div>
            </div>
            {runEnd && (
              <ResearchRunStatus
                runEnd={runEnd}
                // Nur die letzte Frage des Chats: eine neue Antwort gehört direkt unter sie.
                onRetry={message === lastDisplayedMessage ? () => { void retryRun(message, runEnd); } : undefined}
                disabled={composerBusy}
              />
            )}
            </React.Fragment>
            );
          })}

          {effectiveIsLoading && (
            <div className="flex justify-start">
              <div className="min-w-0 max-w-3xl bg-warm-card dark:bg-gray-800 border border-warm-200 dark:border-gray-700 rounded-2xl p-4 shadow-soft">
                {currentResearch?.useLaneB && (
                  <StagingLaneRunLabel
                    requested={retriedLaneSelection(currentResearch.laneVariant, currentResearch.modelPreset)}
                    confirmed={liveSearch?.lane}
                    developer={isDeveloper}
                  />
                )}
                {liveSearch?.restartNotice && <ResearchRestartNotice notice={liveSearch.restartNotice} />}
                {liveSearch?.answer && renderAssistantMessageContent({
                  role: 'assistant',
                  content: liveSearch.answer,
                  timestamp: new Date(),
                  sources: liveSearch.sources,
                  reviewAnnotation: liveSearch.reviewAnnotation,
                  verifiedSources: liveSearch.verifiedSources,
                  researchMetadata: liveResearchMetadata,
                }, displayedMessages.length, { streaming: true })}
                {liveSearch?.answer && renderResearchSources({
                  role: 'assistant',
                  content: liveSearch.answer,
                  timestamp: new Date(),
                  sources: liveSearch.sources,
                  researchMetadata: liveResearchMetadata,
                })}
                {liveSearch?.answer && renderResearchReview({
                  role: 'assistant',
                  content: liveSearch.answer,
                  timestamp: new Date(),
                  reviewAnnotation: liveSearch.reviewAnnotation,
                  verifiedSources: liveSearch.verifiedSources,
                  researchMetadata: liveResearchMetadata,
                })}
                <div className={`${liveSearch?.answer ? 'mt-4 border-t border-warm-200 pt-3 dark:border-gray-700' : ''} flex items-start space-x-2`}>
                  <div className="mt-1 flex space-x-1">
                    <div className="w-2 h-2 bg-accent rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <div>
                    <p className="text-muted dark:text-gray-400 text-sm">{liveSearch?.statusMessage || 'Rechtsquellen werden durchsucht …'}</p>
                    {liveSearch && liveSearch.toolNames.length > 0 && (
                      <p className="mt-1 text-xs text-muted dark:text-gray-500">Werkzeuge: {toolNamesLabel(liveSearch.toolNames)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-red-700 dark:text-red-400 text-sm">{error}</span>
                </div>
                {/* Profil-Nachbau F-06 (BAU-HILFE.md A-10, PF-316; TEIL-3 SP3-01 „Weitere Einstiege“): ins Formular mit Seite und
                    Fehlercode; der Link-Baustein merkt sich die Recherche als Herkunft für „Zurück zum Chat“. */}
                {meldeFehlercode && hilfeBereichGebaut('tickets') && (
                  <BereichLink to={problemMeldenAdresse(location.pathname, meldeFehlercode)}
                    className="ml-7 inline-flex items-center min-h-11 text-sm font-medium text-red-700 dark:text-red-400 underline underline-offset-2 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700">
                    {TICKET_TEXTE.problemMelden}
                  </BereichLink>
                )}
              </div>
            )}

            {currentResearch?.status === 'failed' && !currentResearch.runEnd && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                <span className="text-sm text-red-700 dark:text-red-400">{currentResearch.error}</span>
                <button type="button" onClick={() => { void retryFailedSearch(); }} className="shrink-0 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30">
                  Erneut versuchen
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="bg-warm-card dark:bg-gray-800 border-t border-warm-200 dark:border-gray-700 p-4">
            {sessionShowsSourcesFooter(footerMessages) && (
              <SourcesFooter
                sessionId={sessionId}
                messages={footerMessages}
                onOpenSource={(target, highlight) => {
                  void openViewerSource(target, highlight, { openViewer, openDocumentViewer });
                }}
              />
            )}
            {/* Die Leiste bleibt auch dann stehen, wenn der Knopf gerade nicht
                angeboten wird: nur an ihr lässt sich ein Anhang wieder
                entfernen, der sonst die Frage blockierte. */}
            <ChatContextChips
              documents={contextDocuments}
              disabled={composerBusy || uploadingCount > 0}
              onToggle={(attachmentId, inContext) => { void handleContextToggle(attachmentId, inContext); }}
              onReupload={handleContextReupload}
            />
            <ChatAttachmentChips
              attachments={attachments}
              pendingCount={uploadingCount}
              disabled={composerBusy || uploadingCount > 0}
              onRemove={(attachmentId) => { void handleAttachmentRemove(attachmentId); }}
            />
            {(attachments.length > 0 || uploadingCount > 0) && (
              <p className="mb-2 text-xs text-muted dark:text-gray-400">{CHAT_CONTEXT_PRIVACY_NOTE}</p>
            )}
            {attachmentError && (
              <p className="mb-2 text-xs text-red-600 dark:text-red-400" role="alert">{attachmentError}</p>
            )}
            {/* F-23 (PF-NB-rahmen-4): unter 640 px steht das Feld allein in der ersten Zeile, darunter Anhang, Vorlage und Senden. */}
            <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3 sm:flex-nowrap">
              {mitAnhang && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={chatAttachmentAccept(laneSelection.variant).accept}
                    className="hidden"
                    onChange={(event) => { void handleAttachmentSelection(event.target.files); }}
                  />
                  <input
                    ref={reuploadInputRef}
                    type="file"
                    accept={chatAttachmentAccept(laneSelection.variant).accept}
                    className="hidden"
                    onChange={(event) => { void handleReuploadSelection(event.target.files); }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={composerBusy || uploadingCount > 0 || attachments.length >= CHAT_ATTACHMENT_MAX_OPEN}
                    aria-label="Dateien anhängen"
                    title={`Dateien anhängen (${chatAttachmentAccept(laneSelection.variant).label}; höchstens 50 MB je Datei). ${CHAT_CONTEXT_PRIVACY_NOTE}`}
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-warm-200 bg-white text-muted transition-colors hover:border-accent hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:text-white"
                  >
                    <Paperclip className="h-5 w-5" aria-hidden="true" />
                  </button>
                </>
              )}
              <div className="relative min-w-0 basis-full sm:basis-auto sm:flex-1 max-sm:-order-1">
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => { setInputValue(e.target.value); autoResizeTextarea(); }}
                  onKeyDown={handleKeyDown}
                  placeholder={schmal ? 'Ihre Rechtsfrage …' : 'Beschreiben Sie Ihre konkrete Rechtsfrage …'}
                  rows={1}
                  className="w-full max-sm:min-h-[4.75rem] border border-warm-200 dark:border-gray-600 rounded-xl px-4 py-3 focus:ring-2 focus:ring-accent focus:border-accent bg-white dark:bg-gray-700 text-primary dark:text-white placeholder-muted dark:placeholder-gray-400 transition-all resize-none overflow-y-auto"
                  style={{ maxHeight: '180px' }}
                  disabled={composerBusy}
                />
              </div>
              {/* F-20.3 (5.2): „Vorlage einfügen“ als Symbolknopf, gesperrt wie das Feld. Die Auswahl liegt im Formular: Enter in ihrem
                  Suchfeld schickt die Nachfrage nie ab. */}
              <div className="max-sm:ml-auto" onKeyDown={event => { if (event.key === 'Enter' && event.target instanceof HTMLInputElement) event.preventDefault(); }}>
                <VorlagenKnopf mitText={false} feldHatText={inputValue.trim().length > 0} disabled={composerBusy}
                  onWahl={wahl => vorlageEinfuegen(wahl.text)} />
              </div>
              <div className="flex flex-col items-center space-y-1">
                {composerBusy ? (
                  <button
                    type="button"
                    onClick={() => sessionId && cancelSearch(sessionId)}
                    aria-label={kompakt ? 'Abbrechen' : undefined}
                    title={kompakt ? 'Abbrechen' : undefined}
                    className={`inline-flex items-center justify-center rounded-[3px] bg-red-600 text-sm font-semibold text-white transition-colors hover:bg-red-700 ${kompakt ? 'h-12 w-12' : 'min-w-[7rem] px-5 py-3'}`}
                  >
                    {kompakt ? <Square className="h-4 w-4" fill="currentColor" aria-hidden="true" /> : 'Abbrechen'}
                  </button>
                ) : (
                  <button
                    type="submit"
                    // Solange eine Datei noch gelesen wird, bleibt der Knopf
                    // zu: die Frage soll ihre Unterlagen mitnehmen.
                    disabled={!inputValue.trim() || uploadingCount > 0}
                    aria-label={kompakt ? 'Senden' : undefined}
                    title={kompakt ? 'Senden' : undefined}
                    className={`btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed ${kompakt ? 'h-12 w-12 !p-0' : 'min-w-[7rem] px-5 py-3'}`}
                  >
                    {kompakt ? <Send className="h-5 w-5" aria-hidden="true" /> : 'Senden'}
                  </button>
                )}
              </div>
            </form>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mt-1.5 px-1">
              {/* F-23 (Fehlersuche Nr. 12, F-09): ein echter Schalter mit dem Wort der Präferenzen; daneben, was gerade gilt. */}
              <button
                type="button"
                role="switch"
                aria-checked={chatMode}
                onClick={toggleChatMode}
                className="flex min-h-6 items-center space-x-1.5 whitespace-nowrap rounded-[3px] py-1 text-xs text-muted transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:text-gray-400 dark:hover:text-gray-200"
              >
                <span aria-hidden="true" className={`inline-block w-8 h-4 rounded-full relative transition-colors ${chatMode ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'}`}>
                  <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all duration-200 ${chatMode ? 'left-[18px]' : 'left-[2px]'}`} />
                </span>
                <span>{PRAEFERENZEN_TEXTE.enter}</span>
              </button>
              <span className="text-xs text-muted dark:text-gray-400">
                {chatMode ? PRAEFERENZEN_TEXTE.enterAn : PRAEFERENZEN_TEXTE.enterAus}
              </span>
            </div>
          </div>
    </div>
  );
};

export default RISAssistant;
